import React from 'react'

import { Box, BoxProps, Chip, FormLabel, IconButton } from '@material-ui/core'

import { DateInput, DateInputProps, DateRangePresetInputProps, InlineDateRangePresetInput } from '../../Inputs'

import { MenuOption, RoundedPlainTextButtonMenu } from '../../Menus'

import { xor } from 'lodash-es'

import { ClassNameMap } from '@material-ui/core/styles/withStyles'
import { ArrowDropDown, MoreHoriz } from '@material-ui/icons'
import { IconButtonPopover, IconButtonPopoverProps } from '../../Popovers'


export type FilterComponentType = (
  | 'MultiOptionGroup'
  | 'SingleOptionGroup'
  | 'Date'
  | 'DateRange'
)

export type FilterComponentClassName = (
  | 'editButton'
  | 'dialogContentDividers'
  | 'filterButton'
  | 'filterContainer'
  | 'filterDisplay'
  | 'filterLabel'
  | 'filterContent'
  | 'filterChip'
  | 'filterContextMenuPopover'
)


export type FilterComponentChangeHandler<T = any> = (value: T) => void

export type FilterComponentPropsBase<T> = {
  name: string
  color: 'primary' | 'secondary'
  value: T
  renderLabel?: (value: any) => React.ReactNode
  onChange: FilterComponentChangeHandler<T>
  classes: ClassNameMap<FilterComponentClassName>
  BoxProps?: BoxProps
  areValuesEqual?: (a: T, b: T) => boolean
}

export type MultiOptionFilterComponentProps<T = any> = (
  FilterComponentPropsBase<Array<T>> & {
    options: MenuOption<T>[]
    contextMenuContent?: IconButtonPopoverProps['children']
  }
)

export type SingleOptionFilterComponentProps<T = any> = (
  Omit<FilterComponentPropsBase<T>, 'options'> & {
    options: MenuOption<T>[]
  }
)

export type DateRangeFilterComponentProps<T extends any = any> = (
  Omit<FilterComponentPropsBase<T>, 'onChange' | 'options' | 'areValuesEqual'>
  & DateRangePresetInputProps
)

export type DateFilterComponentProps<T extends any = any> = (
  Omit<FilterComponentPropsBase<T>, 'onChange' | 'options' | 'areValuesEqual'>
  & DateInputProps
)

export type FilterComponentProps = (
  | MultiOptionFilterComponentProps
  | SingleOptionFilterComponentProps
  | DateFilterComponentProps
  | DateRangeFilterComponentProps
)


const defaultFilterComponentBoxProps: BoxProps = {
  display: 'flex',
  alignItems: 'center',
}

function referentialEquality<T = any>(a: T, b: T): boolean { return a === b }


export function MultiOptionGroupFilterComponent<T = any>({
  color,
  name,
  value,
  options,
  renderLabel,
  classes,
  onChange,
  BoxProps = {},
  areValuesEqual = referentialEquality,
  contextMenuContent = null,
}: MultiOptionFilterComponentProps<T>): JSX.Element {
  return (
    <Box {...defaultFilterComponentBoxProps} flexWrap='wrap' {...BoxProps}>
      <FormLabel className={classes.filterLabel}>{ name }</FormLabel>
      { !!contextMenuContent && (
        <IconButtonPopover
          IconComponent={MoreHoriz}
          className={classes.filterContextMenuPopover}>
          { contextMenuContent }
        </IconButtonPopover>
      )}
      { (options || []).map( (option, i) => {
        const selected = value.includes(option.value)
        return (
          <Chip
            key={`${name}-${i}`}
            size='small'
            disabled={option.disabled}
            color={selected ? color : 'default'}
            className={classes.filterChip}
            label={renderLabel ? renderLabel(option.value) : option.label}
            onClick={(): void => {
              const update = xor([option.value], value as T[])
              onChange(update)
            }} />
        )
      })}
    </Box>
  )
}


export function SingleOptionGroupFilterComponent<T = any>({
  name,
  value,
  renderLabel,
  options,
  onChange,
  classes,
  BoxProps = {},
  areValuesEqual = referentialEquality,
}: SingleOptionFilterComponentProps<T>): JSX.Element {
  return (
    <Box {...defaultFilterComponentBoxProps} flexDirection='row' {...BoxProps}>
      <FormLabel className={classes.filterLabel}>{ name }</FormLabel>
      <RoundedPlainTextButtonMenu
        TriggerProps={{
          variant: 'contained',
          size: 'small',
          endIcon: <ArrowDropDown />,
          classes: { root: classes.filterButton },
        }}
        value={value}
        label={renderLabel ? renderLabel(value) : value}
        isEqual={areValuesEqual}
        options={options}
        onChange={(e, value): void => onChange(value)} />
    </Box>
  )
}

export function DateRangeFilterComponent<T = any>({
  name,
  classes,
  BoxProps = {},
  ...props
}: DateRangeFilterComponentProps<T>): JSX.Element {
  return (
    <Box {...defaultFilterComponentBoxProps} flexDirection='row' {...BoxProps}>
      <FormLabel className={classes.filterLabel}>{ name }</FormLabel>
      <InlineDateRangePresetInput
        TriggerProps={{
          classes: { root: classes.filterButton },
        }}
        {...props} />
    </Box>
  )
}

export function DateFilterComponent<T = any>({
  name,
  classes,
  BoxProps = {},
  ...props
}: DateFilterComponentProps<T>): JSX.Element {
  return (
    <Box {...defaultFilterComponentBoxProps} flexDirection='row' {...BoxProps}>
      <FormLabel className={classes.filterLabel}>{ name }</FormLabel>
      <DateInput
        variant='inline'
        {...props} />
    </Box>
  )
}